import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const KnowledgeBasePanel = ({ articles }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);

  const filteredArticles = articles?.filter(article => {
    const matchesCategory = selectedCategory === 'all' || article?.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      article?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      article?.content?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      article?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const categories = ['all', 'technical', 'billing', 'compliance', 'training', 'general'];

  return (
    <div className="p-6">
      {selectedArticle ? (
        <div>
          {/* Article Detail View */}
          <button
            onClick={() => setSelectedArticle(null)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mb-4"
          >
            <Icon name="ArrowLeft" size={16} />
            <span className="text-sm font-medium">Back to Knowledge Base</span>
          </button>
          <div className="bg-muted/50 rounded-lg p-6">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-foreground mb-2">{selectedArticle?.title}</h2>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                  {selectedArticle?.category?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Eye" size={14} />
                  {selectedArticle?.viewCount} views
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="ThumbsUp" size={14} />
                  {selectedArticle?.helpfulCount} found helpful
                </span>
              </div>
            </div>
            <div className="prose prose-sm max-w-none mb-6">
              <p className="text-foreground whitespace-pre-wrap leading-relaxed">{selectedArticle?.content}</p>
            </div>
            {selectedArticle?.tags?.length > 0 && (
              <div className="mb-6">
                <p className="text-sm font-medium text-muted-foreground mb-2">Tags:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedArticle?.tags?.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-muted text-foreground rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="pt-6 border-t border-border">
              <p className="text-sm font-medium text-foreground mb-3">Was this article helpful?</p>
              <div className="flex gap-2">
                <button className="flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-600 rounded-md hover:bg-green-500/20 transition-colors">
                  <Icon name="ThumbsUp" size={16} />
                  <span className="text-sm font-medium">Yes</span>
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-600 rounded-md hover:bg-red-500/20 transition-colors">
                  <Icon name="ThumbsDown" size={16} />
                  <span className="text-sm font-medium">No</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* Search and Filter */}
          <div className="mb-6">
            <div className="relative mb-4">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search articles, guides, and tutorials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-3 border border-border rounded-md bg-background text-foreground"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {categories?.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {category === 'all' ? 'All Articles' : category?.replace(/\b\w/g, l => l?.toUpperCase())}
                </button>
              ))}
            </div>
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredArticles?.length === 0 ? (
              <div className="col-span-3 text-center py-12">
                <Icon name="BookOpen" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-foreground font-medium">No articles found</p>
                <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or category filter</p>
              </div>
            ) : (
              filteredArticles?.map(article => (
                <div
                  key={article?.id}
                  onClick={() => setSelectedArticle(article)}
                  className="bg-muted/50 border border-border rounded-lg p-5 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="bg-primary/10 p-2 rounded-md">
                      <Icon name="FileText" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground mb-2 line-clamp-2">{article?.title}</h4>
                      <span className="inline-block px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                        {article?.category?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                    {article?.content?.substring(0, 120)}...
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="Eye" size={12} />
                      {article?.viewCount}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="ThumbsUp" size={12} />
                      {article?.helpfulCount}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBasePanel;